<script lang="ts">
export default {
  name: 'StockDropForm',
};

// extend the numericString validator to ensure the value is greater than 0
const numericAndGreaterThanZero = numericString.refine(
  (val) => {
    return parseFloat(val) > 0;
  },
  { message: 'Value must be greater than 0' },
);


const validFormSchema = z.object({
  name: z.string().min(1, { message: 'Model Name is required' }),
  accountName: z.string().min(1, { message: 'Company Name is required' }),
  accountID: z.string().optional(),
  asOfDate: z.coerce.date(),
  twoYearHigh: numericAndGreaterThanZero,
  shareOutstanding: numericAndGreaterThanZero,
  insiderOwnership: numericAndGreaterThanZero,
  abcLimits: numericAndGreaterThanZero,
  sideALimits: numericAndGreaterThanZero,
  retention: numericAndGreaterThanZero,
  defenseCosts: numericAndGreaterThanZero,
  scenarioOne: numericAndGreaterThanZero,
  scenarioTwo: numericAndGreaterThanZero,
  scenarioThree: numericAndGreaterThanZero,
  scenarioFour: numericAndGreaterThanZero,
  settlementData: z.string(),
  sideA: z.boolean(),
});

export type Form = z.infer<typeof validFormSchema>;
</script>

<script setup lang="ts">
import DatePicker from '@/components/DatePicker.vue';
import TextInput from '@/components/TextInput.vue';
import Toggle from '@/components/v2/Toggle.vue';
import { computed, reactive, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {type DateValue, parseDate } from '@internationalized/date';
import { ref } from 'vue';
import { z } from 'zod';
import { numericString } from '@/utilities/Validators';
import { analyticsService, referenceService } from '@/services/api';
import { CircleCheck, TriangleAlert } from '@lucide/vue';
import { AccountProgramData } from '@/types/Analytics';
import { accountService, stockDropModelService } from '@/services/api';
import { SelectOption } from '@/types/components/Select';
import Combobox from '@/components/v2/Combobox.vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import AppModal from '@/components/v2/AppModal.vue';
import { PublicCompanySearchResult } from '@/types/Account.js';

// ------
// emits
// ------

const emit = defineEmits(['updated']);

const route = useRoute();
const router = useRouter();

// -----
// refs
// -----
const clients = ref<SelectOption[]>([]);
const selectedCompanyId = ref<string>('');
const loading = ref(true);
const publicDataLoading = ref(false);
const validatingAccount = ref(false);

const stockExchanges = ref<SelectOption[]>([]);
const stockExchangeNameMap = ref<Map<string, string>>(new Map<string, string>());

const publicData = reactive({
  showCompanyPickerModal: false,
  companySearchResults: [] as PublicCompanySearchResult[],
  isAccountValidated: false,
  companySearchLoading: false,
  companySearchSelection: undefined as number | undefined,
  validatedTicker: undefined as string | undefined,
  validatedExchange: undefined as string | undefined,
  enteredTicker: '',
  enteredExchange: '',
  capIQId: undefined as number | undefined
})

const isPublicDataFetchError = ref(false);

const form = reactive({
  name: '',
  accountID: '',
  accountName: '',
  asOfDate: '',
  twoYearHigh: '',
  shareOutstanding: '',
  insiderOwnership: '',
  abcLimits: '',
  sideALimits: '',
  retention: '',
  defenseCosts: '',
  scenarioOne: '',
  scenarioTwo: '',
  scenarioThree: '',
  scenarioFour: '',
  settlementData: 'SCA',
  sideA: false,
});

const formErrors = reactive({
  name: '',
  accountName: '',
  asOfDate: '',
  twoYearHigh: '',
  shareOutstanding: '',
  insiderOwnership: '',
  abcLimits: '',
  sideALimits: '',
  retention: '',
  defenseCosts: '',
  scenarioOne: '',
  scenarioTwo: '',
  scenarioThree: '',
  scenarioFour: '',
  settlementData: '',
  sideA: '',
});

const validatedForm = ref<Form>();

const programData = ref<AccountProgramData | undefined>(undefined);

// ---------
// computed
// ---------
const getPublicDataButtonEnabled = computed(() => {
  return !!asOfDate.value && !!publicData.capIQId && publicData.isAccountValidated;
});

const asOfDate = computed({
  get() {
    if (form.asOfDate === '') {
      return undefined;
    }
    return parseDate(form.asOfDate);
  },
  set(val: DateValue) {
    form.asOfDate = val?.toString() ?? '';
  },
});

const isEditing = computed(() => {
  return !!route.params.stockdropId;
});

const accountProgramDataExists = computed(() => {
  if (!route.params.id) return false;
  if (!programData.value) return false;

  const data = programData.value;
  return data.abcLimits || data.sideALimits || data.retention;
});

const accountId = computed(() => {
  return route.params.id as string || null;
});

const companyValidationModalTitle = computed(() => {
  if (publicData.companySearchLoading) {
    return 'Searching...';
  }

  if (publicData.enteredExchange && publicData.enteredTicker) {
    return `Results for ${stockExchangeNameMap.value.get(publicData.enteredExchange) || ''}: ${publicData.enteredTicker.toUpperCase()}`;
  }

  return 'Results';
})

const isTickerInfoValid = computed(() => publicData.enteredTicker && publicData.enteredExchange);

// ----------
// functions
// ----------
async function refreshData() {
  loading.value = true;

  let loadedModelAccountId: string | undefined;
  
  if (route.params.stockdropId) {
    const model = await stockDropModelService.findById(route.params.stockdropId as string);
    form.name = `${model?.name}`;
    form.accountID = model?.accountId ? `${model.accountId}` : '';
    form.accountName = `${model?.accountName}`;
    form.asOfDate = `${model?.asOfDate?.split('T', 1)[0]}`;
    form.twoYearHigh = `${model?.twoYearHigh}`;
    form.shareOutstanding = `${model?.shareOutstanding}`;
    form.insiderOwnership = `${model?.insiderOwnership}`;
    form.abcLimits = `${model?.abcLimits}`;
    form.sideALimits = `${model?.sideALimits}`;
    form.retention = `${model?.retention}`;
    form.defenseCosts = `${model?.defenseCosts}`;
    form.scenarioOne = `${model?.scenarioOne}`;
    form.scenarioTwo = `${model?.scenarioTwo}`;
    form.scenarioThree = `${model?.scenarioThree}`;
    form.scenarioFour = `${model?.scenarioFour}`;
    form.settlementData = `${model?.settlementData}`;
    form.sideA = model?.sideA || false;
  }

  await populateClients();

  // When editing, set selectedCompanyId from the loaded model's accountID
  // This works for both account analytics and analytics hub routes
  if (isEditing.value && form.accountID) {
    const client = clients.value.find((client) => client.value === form.accountID);
    if (client) {
      selectedCompanyId.value = client.value;
      // Load account info for public data fields
      const accountInfo = await accountService.findById(form.accountID);
      if (accountInfo) {
        publicData.enteredTicker = accountInfo.stockTicker || '';
        publicData.enteredExchange = accountInfo.stockExchange || '';
        publicData.isAccountValidated = accountInfo.capIQId !== null || false;
        if (publicData.isAccountValidated) {
          publicData.validatedTicker = publicData.enteredTicker;
          publicData.validatedExchange = publicData.enteredExchange;
          publicData.capIQId = accountInfo.capIQId;
        }
      }
      // Load program data if accountID exists
      const data = await analyticsService.getAccountProgramData(form.accountID);
      programData.value = data;
    }
  }

  // if coming from account analytics (new form), pre-populate the account name
  if (!isEditing.value && accountId.value) {
    const client = clients.value.find((client) => client.value === accountId.value?.toString());
    if (client) {
      await handleAccountSelect(client);
    }
  }

  // Load program data for account analytics route (new form only)
  if (!isEditing.value && accountId.value) {
    const data = await analyticsService.getAccountProgramData(accountId.value?.toString())
    programData.value = data;

    if (data.abcLimits) form.abcLimits = `${data.abcLimits}`;
    if (data.sideALimits) form.sideALimits = `${data.sideALimits}`;
    if (data.retention) form.retention = `${data.retention}`;
  }

  loading.value = false;
}

async function populateClients() {
  const accountList = await accountService.list();
  clients.value = accountList.map((account) => {
    return {
      label: account.accountName,
      value: account.accountID.toString(),
    };
  });
}

async function handleAccountSelect(account: SelectOption) {
  selectedCompanyId.value = account.value;
  form.accountName = account.label;
  form.accountID = account.value;

  const [data, accountInfo] = await Promise.all([
    analyticsService.getAccountProgramData(account.value),
    accountService.findById(account.value),
  ]);

  publicData.enteredTicker = accountInfo?.stockTicker || '';
  publicData.enteredExchange = accountInfo?.stockExchange || '';
  publicData.isAccountValidated = accountInfo?.capIQId !== null || false;

  if (publicData.isAccountValidated) {
    publicData.validatedTicker = publicData.enteredTicker;
    publicData.validatedExchange = publicData.enteredExchange;
    publicData.capIQId = accountInfo?.capIQId;
  }

  programData.value = data;

  if (data.abcLimits) form.abcLimits = `${data.abcLimits}`;
  if (data.sideALimits) form.sideALimits = `${data.sideALimits}`;
  if (data.retention) form.retention = `${data.retention}`;
}

async function handleSubmit() {
  for (const key in formErrors) {
    formErrors[key] = '';
  }

  const result = validFormSchema.safeParse(form);
  if (!result.success) {
    console.error(result.error.formErrors.fieldErrors);
    for (const [field, error] of Object.entries(result.error.formErrors.fieldErrors)) {
      formErrors[field] = error[0];
    }
    return;
  }

  validatedForm.value = result.data;

  let id;
  const categoryId = route.params.categoryId as string;

  if (isEditing.value) {
    id = route.params.stockdropId;
    await stockDropModelService.update({ ...validatedForm.value, id: id });
    // Emit updated event to refresh the parent list view
    emit('updated');
  } else {
    id = await stockDropModelService.insert(validatedForm.value);
  }

  const saveRoute = accountId.value ?
      `/account/${accountId.value}/analytics/category/${categoryId}/stockdrop/${id}` :
      `/analytics/category/${categoryId}/stockdrop/${id}`;
  return router.push(saveRoute);
}

async function fetchAccountFinancials() {
  if (!form.asOfDate) {
    formErrors.asOfDate = 'As of Date is required';
    return;
  }

  clearAccountFinancialsError();

  if (publicData.capIQId !== undefined) {
    publicDataLoading.value = true;

    try {
      const { twoYearHigh, sharesOutstanding } = await analyticsService.getAccountFinancials(publicData.capIQId, form.asOfDate);

      form.twoYearHigh = `${twoYearHigh}`;
      form.shareOutstanding = `${sharesOutstanding}`;
    } catch (e: any) {
      isPublicDataFetchError.value = true;

      if (e.response.status === 404) {
        formErrors.asOfDate = 'Public data not found';
      } else {
        formErrors.asOfDate = 'An error occurred while fetching public data';
      }
    }

    publicDataLoading.value = false;
  }
}

async function validateAccount() {
  if (publicData.enteredTicker && publicData.enteredExchange) {
    publicData.showCompanyPickerModal = true;
    publicData.companySearchLoading = true;
    publicData.companySearchResults = await accountService.searchForPublicCompany(publicData.enteredTicker, publicData.enteredExchange);
    publicData.companySearchLoading = false;
  }
  return;
}

function clearAccountFinancialsError() {
  if (isPublicDataFetchError.value) {
    isPublicDataFetchError.value = false;
    formErrors.asOfDate = '';
  }
}

function cancelCompanyValidationModal() {
  publicData.showCompanyPickerModal = false;
  publicData.companySearchSelection = undefined;
}

function confirmCompanyValidationModal() {
  if (publicData.companySearchSelection) {
    publicData.capIQId = publicData.companySearchSelection;
    publicData.enteredTicker = publicData.enteredTicker.toUpperCase();

    publicData.isAccountValidated = true;
    publicData.validatedTicker = publicData.enteredTicker;
    publicData.validatedExchange = publicData.enteredExchange;

    publicData.showCompanyPickerModal = false;
  }
}

// ----------
// lifecycle
// ----------
onMounted(async () => {
  // if coming from account -> analytics
  const accountId = route.params.id as string;

  if (accountId) {
    const data = await analyticsService.getAccountProgramData(accountId)
    programData.value = data;

    if (data.abcLimits) form.abcLimits = `${data.abcLimits}`;
    if (data.sideALimits) form.sideALimits = `${data.sideALimits}`;
    if (data.retention) form.retention = `${data.retention}`;
  }

  // load ref data
  const exchanges = await referenceService.stockExchanges()
  stockExchanges.value = exchanges.map((exchange) => ({
      label: `${exchange.Abbrev}`,
      value: `${exchange.Abbrev}`,
    }));

  exchanges.forEach((exchange) => {
    stockExchangeNameMap.value.set(`${exchange.ID}`, exchange.Abbrev);
  });

  await refreshData();
});

watch(() => publicData.enteredExchange, (newVal) => {
  if (publicData.isAccountValidated && newVal !== publicData.validatedExchange) {
    publicData.isAccountValidated = false;
    publicData.validatedTicker = undefined;
    publicData.validatedExchange = undefined;
    publicData.capIQId = undefined;
  }
});

watch(() => publicData.enteredTicker, (newVal) => {
  if (publicData.isAccountValidated && newVal !== publicData.validatedTicker) {
    publicData.isAccountValidated = false;
    publicData.validatedTicker = undefined;
    publicData.validatedExchange = undefined;
    publicData.capIQId = undefined;
  }
});

</script>

<template>
  <PageSpinner class="loader" v-if="loading" />

  <div v-else class="inner w-full max-w-7xl p-4 text-primary">
    <div class="header">
      <h1 data-testid="form-header">{{ isEditing ? 'Edit' : 'New' }} Stock Drop Model</h1>
    </div>

    <form class="relative grid p-2 lg:grid-cols-[1fr_255px]">
      <div class="flex flex-col gap-10 xl:mr-10">
        <fieldset class="grid grid-cols-1 xl:grid-cols-[225px_1fr]">
          <div class="mb-4">
            <h2>Company Stock Data</h2>
          </div>

          <div class="flex flex-col gap-4 pr-4 mr-4">
            <div class="flex flex-col">
              <label for="model-name">Model Name</label>
              <TextInput
                  id="model-name"
                  data-testid="model-name"
                  class="w-full"
                  :class="{ 'border-red-500': formErrors.name }"
                  v-model="form.name" />
              <p data-testid="name-errors" class="text-xs text-red-500">{{ formErrors.name }}</p>
            </div>

            <div class="flex flex-col">
              <label for="account-name" class="pb-1">Company Name</label>
                <Combobox
                    class="w-full"
                    data-testid="account-name"
                    :input-class="formErrors.accountName ? 'border-red-500' : ''"
                    :disabled="accountId !== null"
                    v-model="selectedCompanyId"
                    @selected="handleAccountSelect"
                    :options="clients" />
              <p data-testid="company-errors" class="text-xs text-red-500">{{ formErrors.accountName }}</p>
            </div>

            <!-- company validation (find matching company with CapIQID -->
            <div class="flex items-center w-full gap-4 justify-between">
              <div class="flex gap-4">
                <div class="flex flex-col">
                  <label for="stock-ticker" class="pb-1">Stock Ticker</label>
                  <TextInput
                    id="stock-ticker"
                    class="w-full min-w-24"
                    :class="{ 'border-red-500': formErrors.name }"
                    v-model="publicData.enteredTicker" />
                  <p class="text-xs text-red-500">{{ formErrors.name }}</p>
                </div>

                <div class="flex flex-col">
                  <label for="stock-exchange" class="pb-1">Stock Exchange</label>
                  <Combobox
                    id="stock-exchange"
                    :input-class="formErrors.accountName ? 'min-w-28 border-red-500' : 'min-w-28'"
                    v-model="publicData.enteredExchange"
                    :options="stockExchanges" />
                  <p class="text-xs text-red-500">{{ formErrors.accountName }}</p>
                </div>
              </div>

              <div class="flex gap-4 items-center">

                <!-- validated account indicator -->
                <div class="h-full flex flex-col">
                  <span class="invisible">&nbsp;</span>
                  <div class="h-full flex items-center justify-center">
                    <CircleCheck :size="25" :class="{'text-green-500' : publicData.isAccountValidated, 'text-gray-300' : !publicData.isAccountValidated}" />
                  </div>
                </div>

                <!-- validate/validated button -->
                <div class="h-full flex flex-col">
                  <span class="invisible">&nbsp;</span>
                  <div class="h-full flex items-center justify-center">
                    <button type="button"
                            :disabled="!isTickerInfoValid"
                            class="btn btn-sm btn-primary min-w-24 text-white"
                            :class="{ 'btn-disabled' : publicData.isAccountValidated }"
                            @click="validateAccount">
                      <span v-if="!validatingAccount">{{ publicData.isAccountValidated ? 'Validated' : 'Validate' }}</span>
                      <span v-else class="loading loading-dots loading-xs"></span>
                    </button>
                  </div>
                </div>

              </div>
            </div>

            <div class="flex flex-col">
              <label for="as-of-date">As of</label>
              <div class="flex items-center w-full">
                <DatePicker
                  id="as-of-date"
                  data-testid="as-of-date"
                  class="!input-md w-full"
                  :class="{ 'border-red-500': formErrors.asOfDate }"
                  @update:modelValue="clearAccountFinancialsError"
                  v-model="asOfDate" />

                <button type="button"
                        :disabled="publicDataLoading"
                        class="ml-4 btn btn-sm btn-primary min-w-32 text-white"
                        :class="{ 'btn-disabled' : !getPublicDataButtonEnabled }"
                        @click="fetchAccountFinancials">
                  <span v-if="!publicDataLoading">Get Public Data</span>
                  <span v-else class="loading loading-dots loading-xs"></span>
                </button>
              </div>

              <p data-testid="date-errors" class="text-xs text-red-500">{{ formErrors.asOfDate }}</p>
            </div>

            <div class="flex flex-col">
              <label for="2-year-high">2 Year High</label>
              <TextInput
                id="2-year-high"
                class="w-full"
                :class="{ 'border-red-500': formErrors.twoYearHigh }"
                isCurrency
                @focus="clearAccountFinancialsError"
                v-model="form.twoYearHigh" />
              <p data-testid="two-year-high-errors" class="text-xs text-red-500">{{ formErrors.twoYearHigh }}</p>
            </div>
            <div class="flex flex-col">
              <label for="share-outstanding">Share Outstanding</label>
              <TextInput
                id="share-outstanding"
                class="w-full"
                :class="{ 'border-red-500': formErrors.shareOutstanding }"
                isNumber
                @focus="clearAccountFinancialsError"
                v-model="form.shareOutstanding" />
              <p data-testid="share-outstanding-errors" class="text-xs text-red-500">{{ formErrors.shareOutstanding }}</p>
            </div>
            <div class="flex flex-col">
              <label for="insider-ownership">Insider Ownership</label>
              <TextInput
                id="insider-ownership"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.insiderOwnership }"
                isPercent
                v-model="form.insiderOwnership" />
              <p class="text-xs text-red-500">{{ formErrors.insiderOwnership }}</p>
            </div>
          </div>
        </fieldset>

        <fieldset class="grid grid-cols-1 xl:grid-cols-[225px_1fr]">
          <div class="mb-4">
            <h2>Program Data</h2>

            <div v-if="route.params.id && !accountProgramDataExists" class="flex items-center gap-2 my-3">
              <TriangleAlert class="text-slate-light" />
              <div class="w-3/4">
                D&O program data is unavailable for this client.
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-4 pr-4 mr-4">
            <div class="flex flex-col">
              <label for="abc-limits">ABC Limits</label>
              <TextInput
                id="abc-limits"
                class="w-full"
                :class="{ 'border-red-500': formErrors.abcLimits }"
                isCurrency
                v-model="form.abcLimits" />
              <p class="text-xs text-red-500">{{ formErrors.abcLimits }}</p>
            </div>
            <div class="flex flex-col">
              <label for="side-a-limits">Side-A Limits</label>
              <TextInput
                id="side-a-limits"
                class="w-full"
                :class="{ 'border-red-500': formErrors.sideALimits }"
                isCurrency
                v-model="form.sideALimits" />
              <p class="text-xs text-red-500">{{ formErrors.sideALimits }}</p>
            </div>
            <div class="flex flex-col">
              <label for="retention">Retention</label>
              <TextInput
                id="retention"
                class="w-full"
                :class="{ 'border-red-500': formErrors.retention }"
                isCurrency
                v-model="form.retention" />
              <p class="text-xs text-red-500">{{ formErrors.retention }}</p>
            </div>
            <div class="flex flex-col">
              <label for="defense-costs">Defense Costs</label>
              <TextInput
                id="defense-costs"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.defenseCosts }"
                isPercent
                v-model="form.defenseCosts" />
              <p class="text-xs text-red-500">{{ formErrors.defenseCosts }}</p>
            </div>
          </div>
        </fieldset>

        <fieldset class="grid xl:grid-cols-[225px_1fr]">
          <h2 class="mb-4">Potential Drops</h2>
          <div class="flex flex-col gap-4 pr-4 mr-4">
            <div>
              <label for="scenario-1">Scenario 1</label>
              <TextInput
                id="scenario-1"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.scenarioOne }"
                isPercent
                v-model="form.scenarioOne" />
              <p class="text-xs text-red-500">{{ formErrors.scenarioOne }}</p>
            </div>
            <div>
              <label for="scenario-2">Scenario 2</label>
              <TextInput
                id="scenario-2"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.scenarioTwo }"
                isPercent
                v-model="form.scenarioTwo" />
              <p class="text-xs text-red-500">{{ formErrors.scenarioTwo }}</p>
            </div>
            <div>
              <label for="scenario-3">Scenario 3</label>
              <TextInput
                id="scenario-3"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.scenarioThree }"
                isPercent
                v-model="form.scenarioThree" />
              <p class="text-xs text-red-500">{{ formErrors.scenarioThree }}</p>
            </div>
            <div>
              <label for="scenario-4">Scenario 4</label>
              <TextInput
                id="scenario-4"
                class="w-full pr-6"
                :class="{ 'border-red-500': formErrors.scenarioFour }"
                isPercent
                v-model="form.scenarioFour" />
              <p class="text-xs text-red-500">{{ formErrors.scenarioFour }}</p>
            </div>
            <div>
              <label for="settlement-data">Settlement Data</label>
              <TextInput
                id="settlement-data"
                class="input-disabled w-full pr-6"
                v-model="form.settlementData"
                readonly />
            </div>
            <div>
              <Toggle id="side-a" label="Side-A" v-model="form.sideA" class="w-fit" />
            </div>
          </div>
        </fieldset>
      </div>
        <div class="sticky top-20 flex flex-col gap-2 px-4">
          <button
            class="btn btn-secondary text-base-200 hover:opacity-100"
            data-testid="submit-button"
            @click.stop.prevent="handleSubmit">
            {{ isEditing ? 'Save' : 'Create' }}
          </button>
          <button class="btn" @click.stop.prevent="router.back()">Cancel</button>
        </div>
    </form>

    <AppModal
      :title="companyValidationModalTitle"
      size="lg"
      actionButtonLabel="Confirm"
      :action-disabled="publicData.companySearchSelection === undefined"
      :show="publicData.showCompanyPickerModal"
      :handle-cancel="cancelCompanyValidationModal"
      :handle-action="confirmCompanyValidationModal"
      modal-class="overflow-y-auto"
    >
      <template v-slot:content>
        <div
          v-if="publicData.companySearchLoading"
          class="flex justify-center mx-auto loading loading-dots loading-lg"
        />

        <template v-if="!publicData.companySearchLoading">
          <div v-if="publicData.companySearchResults.length > 0" class="overflow-y-auto max-h-screen">
            <p class="mb-4">The following companies were found:</p>

            <div v-for="company in publicData.companySearchResults" :key="company.companyId">
              <div class="flex gap-2 mb-4 items-center">
                <input
                  type="radio"
                  name="company"
                  class="radio radio-sm radio-primary border border-solid"
                  :value="company.companyId"
                  @click="publicData.companySearchSelection = company.companyId"
                />
                <div>
                  <p class="font-bold text-lg">{{ company.name }}</p>
                  <div class="flex items-center gap-1">
                    <span class="text-sm font-bold">{{ company.exchangeSymbol }}:</span>
                    <span class="text-sm font-bold">{{ company.tickerSymbol }}</span>
                    <span
                      class="ml-2 uppercase badge text-xs font-bold"
                      :class="{
                          'bg-company-green text-white': company.tickerStatus.toLowerCase() === 'active',
                          'text-gray-600 badge-ghost border-1 border-gray-300': company.tickerStatus !== 'Active',
                        }"
                    >
                        {{ company.tickerStatus }}
                      </span>
                  </div>
                  <div class="text-gray-500 text-sm">{{ company.address }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>No results found.</div>
        </template>

      </template>

    </AppModal>


  </div>
</template>

<style scoped>

.loader {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

</style>
